import React, { useState, useEffect } from 'react'
import styled from 'styled'

import { actionCreators, GettyActionCreators } from '../../../actions'

import { GettyStore } from '../../../store'

import { visitorInfoSchema } from '../../../schemas/visitorInfo'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { FooterEditEntity } from 'components/ui/footer'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { StatusGuard } from 'components/ui/status-guard'
import { Description, Image, Title } from 'components/ui/list-item-label'

import { AppPageTranslations }  from './app-page-translations'
import { ContentPageTranslations }  from './content-page-translations'
import { DeepDivePageTranslations }  from './deep-dive-page-translations'
import { HTMLPageTranslations }  from './html-page-translations'
import { ImagePageTranslations }  from './image-page-translations'
import { URLPageTranslations } from './url-page-translations'

import { LayoutFullWidthPage } from 'layouts/full-width-page'

import { VisitorInfoPage } from 'generated/mos/org/getty/visitorinfo'
import { GetVisitorInfoPageRequest } from 'generated/mos/org/getty/visitorinfoaccess'
import { Media } from 'generated/mos/media'

import { bffEndpoint } from 'helpers/bff'
import { assertNever, pick } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { getPageSubnav } from '../subnav'

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetVisitorInfoPageRequest' | 'gettyUpdateAmenityRequest'> &
  Pick<typeof sharedActionCreators, 'toastNotification'>

type ConnectedProps = Pick<GettyStore['getty'], 'visitorInfoUpdate'>

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly refId: string;
}

type Props = ActionProps & ConnectedProps & DirectProps

const HEADER_HEIGHT = 100

const PageItem = styled.div`
  display: flex;
  width: 100%;
  height: ${HEADER_HEIGHT}px;
  align-items: center;
  padding-top: ${space(8)};
  padding-bottom: ${space(6)};
  border-bottom: 1px solid ${({ theme }) => theme.color.grayL60};
`

const VisitorInfoTranslationsView = (props: Props) => {
  const {
    gettyGetVisitorInfoPageRequest,
    gettyUpdateAmenityRequest,
    historyPush,
    projectId,
    refId,
    visitorInfoUpdate,
    toastNotification,
  } = props;

  const [submit, setSubmit] = useState<() => void>(() => {})
  const [modified, setModified] = useState<boolean | undefined>(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)

  const backLocation = `/project/${projectId}/visitor-info`

  const page = statusSelector.data(visitorInfoUpdate)

  const hostName = bffEndpoint(window.location.hostname)

  // load initial data
  useEffect(() => {
    if (visitorInfoUpdate.status === Status.Idle || page && VisitorInfoPage.mustRef(page.ref).id !== refId) {
      gettyGetVisitorInfoPageRequest({
        type: GetVisitorInfoPageRequest.refName,
        ref: {
          typename: VisitorInfoPage.refName,
          id: refId
        }
      })
    }
  })

  useEffect(() => {
    // if the status is ready and the data had been submitted, trigger success toast.
    if (visitorInfoUpdate.status === Status.Ready && hasSubmitted) {
      toastNotification({ type: 'success', text: 'Translations updated' })
      setHasSubmitted(false)
    }
  }, [ visitorInfoUpdate.status, hasSubmitted, toastNotification ])

  const renderForm = (pageType: VisitorInfoPage.Entity['pageProperties']) => {
    const viewProps = { page, setSubmit, setModified, setHasSubmitted }
    const type = pageType && pageType.type
    if(!type) return null
    switch(type) {
      case VisitorInfoPage.SiteMap.refName :
        return <ImagePageTranslations {...viewProps} />
      case VisitorInfoPage.StaticWebPage.refName :
        return <HTMLPageTranslations {...viewProps} />
      case VisitorInfoPage.URLWebPage.refName :
        return <URLPageTranslations {...viewProps} />
      case VisitorInfoPage.AppPage.refName :
        return <AppPageTranslations {...viewProps} />
      case VisitorInfoPage.AmenityCategory.refName :
        return <ContentPageTranslations {...viewProps} />
      case VisitorInfoPage.DeepDivePage.refName :
        return <DeepDivePageTranslations {...viewProps} />
      case VisitorInfoPage.SectionSeparator.refName :
        return null
      default:
        return assertNever(type)
    }
  }

  const title = page && page.listTitle && visitorInfoSchema.listTitle ? visitorInfoSchema.listTitle.read(page.listTitle) as string : ''

  const image = page && page.listIconRef && visitorInfoSchema.listIconRef.read(page.listIconRef) as Media.Ref
  const imageId = image && image.id

  const imagePath = imageId ? `${hostName}/images/${imageId}.jpg?s=crop&h=92&w=92` : ''

  return (
    <>
      <LayoutFullWidthPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title={`Translate "${title}"`}
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getPageSubnav(projectId, refId)} modified={modified} historyPush={historyPush} >
          </SecondaryNavigation>
        )}
        title={title}
        content={() => (
          <StatusGuard status={visitorInfoUpdate} mode="form">
            {page && renderForm(page.pageProperties)}
          </StatusGuard>
        )}
        footer={() => (
          <FooterEditEntity
            entity={visitorInfoUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit()
              return false
            }}
          />
        )}
      />
    </>
  )
}

export const VisitorInfoTranslations = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'visitorInfoUpdate'),
  {
    ...pick(actionCreators, 'gettyGetVisitorInfoPageRequest', 'gettyUpdateAmenityRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(VisitorInfoTranslationsView)
